import React from 'react';
import {
    Paper,
    Stack,
    Typography,
    TextField,
    Box,
    Alert,
} from '@mui/material';
import { PersonOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * Inline guest details card shown on /checkout when the user is
 * not authenticated and `settings.checkout.allow_guest` is true.
 *
 * The fields are controlled by the parent (Checkout.jsx) so the
 * place-order payload can read them in one place. The backend
 * silently creates a User row for the guest and emails a
 * password-set link after the order — see Backend Sprint 7.
 */
const GuestDetailsCard = ({ values, errors = {}, onChange }) => {
    const { i18n, t } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const setField = (key) => (e) => onChange({ ...values, [key]: e.target.value });

    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <PersonOutline color="primary" />
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {t('checkout.guestTitle', 'Continue as guest')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t(
                            'checkout.guestSubtitle',
                            'We need a few details to confirm your order. You can set a password later.'
                        )}
                    </Typography>
                </Box>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
                <TextField
                    fullWidth
                    required
                    label={t('auth.name', 'Full name')}
                    value={values.name || ''}
                    onChange={setField('name')}
                    error={Boolean(errors.name)}
                    helperText={errors.name?.[0] || errors.name}
                />
                <TextField
                    fullWidth
                    required
                    type="email"
                    label={t('auth.email', 'Email')}
                    value={values.email || ''}
                    onChange={setField('email')}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.[0] || errors.email}
                />
            </Stack>
            <TextField
                fullWidth
                required
                label={t('auth.phone', 'Phone')}
                value={values.phone || ''}
                onChange={setField('phone')}
                placeholder={isArabic ? 'مثال: 01012345678' : 'e.g. 01012345678'}
                error={Boolean(errors.phone)}
                helperText={errors.phone?.[0] || errors.phone}
            />

            <Alert severity="info" sx={{ mt: 2 }} variant="outlined">
                {t(
                    'checkout.guestPasswordEmailHint',
                    "We'll email you a link to set a password after the order is placed."
                )}
            </Alert>
        </Paper>
    );
};

export default GuestDetailsCard;
